// frontend/src/pages/Auth.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Shield, Lock, ArrowRight, Fingerprint } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

function getNextFromSearch(search) {
  try {
    const params = new URLSearchParams(search || "");
    const raw = params.get("next");
    if (!raw) return "/dashboard";

    const decoded = decodeURIComponent(raw);
    if (typeof decoded !== "string") return "/dashboard";
    if (!decoded.startsWith("/")) return "/dashboard";
    if (decoded.startsWith("//")) return "/dashboard";
    return decoded;
  } catch {
    return "/dashboard";
  }
}

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const nextPath = useMemo(() => getNextFromSearch(location.search), [location.search]);

  // Prevent duplicate navigations if auth state change fires multiple times.
  const redirectedRef = useRef(false);

  const goNext = () => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(nextPath || "/dashboard", { replace: true });
  };

  // If already authed (Supabase session exists), go straight to next
  useEffect(() => {
    let mounted = true;
    redirectedRef.current = false;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        if (data?.session) goNext();
      } catch {
        // ignore; user will proceed manually
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) goNext();
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPath]);

  async function handleAuth(e) {
    e.preventDefault();
    setErrorMsg("");
    setBusy(true);

    try {
      const cleanEmail = String(email || "").trim();
      const cleanPw = String(password || "");

      if (!cleanEmail || !cleanPw) {
        setErrorMsg("Email and password are required.");
        return;
      }

      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password: cleanPw,
        });

        if (error) {
          setErrorMsg(error.message || "Login failed.");
          return;
        }
      } else {
        const { error } = await supabase.auth.signUp({
          email: cleanEmail,
          password: cleanPw,
        });

        if (error) {
          setErrorMsg(error.message || "Sign up failed.");
          return;
        }
      }

      // Redirect happens via onAuthStateChange above.
    } catch (err) {
      setErrorMsg(err?.message || "Auth error.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-screen w-full bg-slate-900 font-inter overflow-hidden">
      {/* Left Branding Panel */}
      <div className="hidden lg:flex w-1/2 bg-blue-600 p-20 flex-col justify-between relative">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-12">
            <Shield size={32} className="text-white" />
            <span className="font-black italic uppercase text-2xl tracking-tighter text-white">
              AI Fine Protector ™
            </span>
          </div>
          <h2 className="text-7xl font-black italic uppercase tracking-tighter text-white leading-[0.9]">
            Institutional <br /> Integrity <br /> Starts Here.
          </h2>
        </div>

        <div className="relative z-10 border-l-4 border-white/20 pl-6">
          <p className="text-blue-100 font-bold uppercase tracking-widest text-[10px] mb-2">
            System Status
          </p>
          <p className="text-white font-black italic text-sm uppercase">
            Regulatory Engines: Online
          </p>
        </div>

        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 opacity-50" />
      </div>

      {/* Right Login Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-900">
        <div className="max-w-md w-full">
          <div className="mb-12">
            <h1 className="text-4xl font-black italic uppercase tracking-tighter text-white mb-2">
              {isLogin ? "Access the Vault" : "Initialize Account"}
            </h1>
            <p className="text-slate-500 font-medium">Supabase authentication required.</p>
          </div>

          {errorMsg ? (
            <div className="mb-6 rounded-2xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-200">
              {errorMsg}
            </div>
          ) : null}

          <form onSubmit={handleAuth} className="space-y-6">
            <div className="group">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 block group-focus-within:text-blue-500 transition-colors">
                Corporate Email
              </label>
              <div className="relative">
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  autoComplete="email"
                  className="w-full bg-slate-800/50 border border-slate-700 p-5 rounded-2xl text-white font-bold outline-none focus:border-blue-600 focus:bg-slate-800 transition-all pl-14"
                />
                <Lock
                  size={20}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600"
                />
              </div>
            </div>

            <div className="group">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 block group-focus-within:text-blue-500 transition-colors">
                Password
              </label>
              <div className="relative">
                <input
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  className="w-full bg-slate-800/50 border border-slate-700 p-5 rounded-2xl text-white font-bold outline-none focus:border-blue-600 focus:bg-slate-800 transition-all pl-14"
                />
                <Fingerprint
                  size={20}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full py-6 bg-blue-600 text-white rounded-2xl font-black italic uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 hover:bg-white hover:text-slate-900 transition-all shadow-xl shadow-blue-900/20 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? "Working..." : isLogin ? "Initialize Session" : "Create Credentials"}{" "}
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="mt-10 border-t border-slate-800 pt-8 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-blue-500 transition-colors"
              type="button"
              disabled={busy}
            >
              {isLogin
                ? "Need an account? Create credentials."
                : "Already have access? Return to login."}
            </button>
          </div>

          <div className="mt-10 text-center text-xs text-slate-600">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </div>
    </div>
  );
}